import { MagnifyingGlass } from "@phosphor-icons/react"
import {
  type ColumnDef,
  type ColumnSizingState,
  flexRender,
  getCoreRowModel,
  type SortingState,
  type Row as TableRowType,
  useReactTable,
} from "@tanstack/react-table"
import { Card } from "@tokenterminal/core/ui/layout/card/Card"
import { Tooltip } from "@tokenterminal/core/ui/overlays/tooltip/Tooltip"
import { LoadingSpinner } from "@tokenterminal/core/ui/progress/LoadingSpinner"
import { Skeleton } from "@tokenterminal/core/ui/progress/skeleton/Skeleton"
import * as CoreTable from "@tokenterminal/core/ui/table/Table"
import { toDictionary } from "@tokenterminal/core/utils/toDictionary"
import { trpcClient } from "@tokenterminal/core/utils/trpc"
import { type PresetColumnConfig } from "@tokenterminal/tt-analytics-api-types/dist/api/projects"
import { ProjectLogo } from "components/project/ProjectLogo"
import { DEFAULT_NUMBER_FORMAT } from "components/v3/Header/AccountDropdown/NumberFormatSwitcher"
import { useSocket } from "context/SocketContext"
import { useNumberFormatter } from "hooks/useNumberFormatter"
import { useAtom } from "jotai"
import { capitalize } from "lodash"
import EmptyState from "modules/account/views/Team/EmptyState"
import { useMarketSectorsList } from "modules/chart-builder/components/SeriesTab/SerieSelector/FilterSelector/useMarketSectorsList"
import ChainsDeployedOnLinkOrModal from "modules/market-sectors/DetailPage/MetricsTab/ChainsDeployedOnLinkOrModal"
import { toSlug } from "modules/market-sectors/utils"
import { capitalizeFirstLetter } from "modules/metrics/MetricDetailPage/MetricDetailPage"
import { useMetricsConfigurationList } from "modules/projects/hooks/useMetricsConfigurationList"
import { useMetricMetadataByProjectSlug } from "modules/projects/OverviewTab/useMetricMetadataForProject"
import StandardizedListingIcon from "modules/v3/ProjectHero/StandardizedListingIcon"
import Link from "next/link"
import { Suspense, useCallback, useEffect, useMemo, useState } from "react"
import { twMerge } from "tailwind-merge"
import { useIntersectionObserver } from "utils/useIntersectionObserver"
import { MarketSectorTooltip } from "./MarketSectorTooltip"
import { MiniChartInlineSparkline } from "./MiniChartSparkline"
import { type FiltersAtomType } from "./ScreenerTable"
import SingleChainDisplay from "./SingleChainDisplay"
import { TableHeader } from "./TableHeader"
import { CHART_VALUE_CELL_DATA_TYPES } from "./utils/cellFormatHelper"

const ROWS_PER_FETCH = 50

export const tableNumberCss =
  "min-h-6 flex items-center justify-end tabular-nums"

const sizes: Record<
  "expanded" | "compact",
  {
    PROJECT_LOGO_SIZE: number
    ROW_HEIGHT: number
    MINI_CHART_HEIGHT: number
    fontSizeClassName: string
  }
> = {
  expanded: {
    PROJECT_LOGO_SIZE: 20,
    ROW_HEIGHT: 57,
    MINI_CHART_HEIGHT: 24,
    fontSizeClassName: "text-sm",
  },
  compact: {
    PROJECT_LOGO_SIZE: 16,
    ROW_HEIGHT: 36.5,
    MINI_CHART_HEIGHT: 20,
    fontSizeClassName: "",
  },
}

const TYPES = {
  String: "String",
  Number: "Number",
  ArrayString: "Array(String)",
}

const OPERATORS = {
  like: "like",
  in: "in",
  hasAny: "hasAny",
}

export const leftAlignedColumns = [
  "name",
  "project_name",
  "market_sector_id",
  "chains",
  "chain",
  "market_sectors",
  "definition",
]

const aggregationColumns = [
  "sum",
  "avg",
  "latest",
  "ath",
  "atl",
  "change",
  "trend",
  "sparkline",
]

export const fetchProjectArgs = async ({
  marketSectors,
  chains,
  search,
  columns,
  sorting,
  filters: columnFilters,
}: {
  marketSectors: string[]
  chains: string[]
  search: string
  columns: PresetColumnConfig[]
  sorting: SortingState
  filters?: { field: string; operator: string; value: number | number[] }[]
}) => {
  let sort
  const filters: Record<string, any> = {}

  if (sorting.length) {
    sort = {
      column: sorting[0]?.id,
      desc: sorting[0]?.desc,
    }
  }

  if (search) {
    const searchFilter = {
      type: TYPES.String,
      value: search,
      operator: OPERATORS.like,
    }
    filters.name = { or: [searchFilter] }
    filters.project_id = { or: [searchFilter] }
  }

  if (marketSectors.length) {
    filters.market_sectors = {
      and: [
        {
          type: TYPES.ArrayString,
          value: marketSectors,
          operator: OPERATORS.hasAny,
        },
      ],
    }
  }

  if (chains.length) {
    filters.chains = {
      and: [
        {
          type: TYPES.ArrayString,
          value: chains,
          operator: OPERATORS.hasAny,
        },
      ],
    }
  }

  if (columnFilters && columnFilters.length > 0) {
    columnFilters.forEach((filter) => {
      if (filters[filter.field]) {
        filters[filter.field].and.push({
          type: Array.isArray(filter.value) ? "Array(Float64)" : "Float64",
          value: filter.value,
          operator: filter.operator,
        })
      } else {
        filters[filter.field] = {
          and: [
            {
              type: Array.isArray(filter.value) ? "Array(Float64)" : "Float64",
              value: filter.value,
              operator: filter.operator,
            },
          ],
        }
      }
    })
  }

  const cols = columns
    .filter((c) => !c.virtual_field)
    .map((c) => {
      const field = c.field.split("_")

      const aggregation = field.pop()
      const interval = field.pop()
      const metric = field.join("_")

      if (aggregationColumns.includes(aggregation ?? "")) {
        return {
          metric,
          interval,
          aggregation: c.sparkline ? "sparkline" : aggregation,
        }
      }

      // We will handle this in the table component
      if (c.field === "chain" || c.field === "market_sectors") {
        return
      }

      if (c.field === "market_sector_id") {
        return {
          name: "market_sectors",
        }
      }

      return {
        name: c.field,
      }
    })
    .filter((c) => c !== undefined) as (
    | {
        name: string
      }
    | {
        interval: "24h" | "7d" | "30d" | "90d" | "max" | "180d" | "365d"
        metric: string
        aggregation:
          | "avg"
          | "trend"
          | "change"
          | "sum"
          | "latest"
          | "ath"
          | "atl"
          | "sparkline"
      }
  )[]

  return {
    columns: cols,
    sorting: sort,
    filters,
  }
}

export const fetchProjectsList = async (
  {
    marketSectors,
    chains,
    search,
    columns,
    sorting,
    filters: columnFilters,
  }: {
    marketSectors: string[]
    chains: string[]
    search: string
    columns: PresetColumnConfig[]
    sorting: SortingState
    filters: { field: string; operator: string; value: number | number[] }[]
  },
  limit: number,
  offset: number,
  ignoreThreshold?: boolean
) => {
  const {
    columns: cols,
    sorting: sort,
    filters,
  } = await fetchProjectArgs({
    marketSectors,
    chains,
    search,
    columns,
    sorting,
    filters: columnFilters,
  })

  return trpcClient.projects.postScreenerTable.mutate({
    columns: cols,
    sorting: sort,
    filters,
    offset,
    limit,
    ignore_threshold: ignoreThreshold,
  })
}

function LiveValueCell({
  fontSizeClassName,
  value,
  projectId,
  cellDataType,
}: {
  fontSizeClassName: string
  value: number
  projectId: string
  cellDataType: string
}) {
  const socketData = useSocket()

  const realtimeValue = socketData.priceMap[projectId]?.usd

  const priceValue = CHART_VALUE_CELL_DATA_TYPES[cellDataType](
    realtimeValue || value
  )

  return (
    <div
      className={twMerge(tableNumberCss, fontSizeClassName)}
      style={{
        fontFamily: "var(--font-geist-mono)",
      }}
    >
      <span className="truncate">
        {priceValue === "0" || priceValue === "0.0%" ? "" : priceValue}
      </span>
    </div>
  )
}

const placeholderArray = Array.from({ length: 50 }, () => ({
  placeholder: true,
}))

export function TableComponent({
  columns,
  filtersAtom,
  compact,
  tableContainerRef,
  filterItemId,
  ignoreThreshold,
}: {
  columns: PresetColumnConfig[]
  filtersAtom: FiltersAtomType
  compact: boolean
  tableContainerRef: React.RefObject<HTMLDivElement>
  filterItemId?: string
  ignoreThreshold?: boolean
}) {
  const numberFormat = useNumberFormatter()
  const { data: marketSectors = [] } = useMarketSectorsList()
  const { data: metricMetadataByProjectSlug = {} } =
    useMetricMetadataByProjectSlug()
  const marketSectorDictionary = toDictionary(marketSectors, "id")
  const { data: metrics = [] } = useMetricsConfigurationList()
  const metricDictionary = toDictionary(metrics, "metric_id")

  const [projects, setProjects] = useState<any[]>(placeholderArray)
  const [isFetching, setIsFetching] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [offset, setOffset] = useState(0)

  const [filters, setFilters] = useAtom(filtersAtom)
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(
    filters.columnSizing
  )

  const size = compact ? sizes.compact : sizes.expanded

  const height = size.ROW_HEIGHT
  const projectLogoSize = size.PROJECT_LOGO_SIZE
  const fontSizeClassName = size.fontSizeClassName
  const miniChartHeight = size.MINI_CHART_HEIGHT

  useEffect(() => {
    setColumnSizing(filters.columnSizing)
  }, [filters.columnSizing])

  useEffect(() => {
    const handleScroll = () => {
      if (tableContainerRef.current) {
        const isScrolled = tableContainerRef.current.scrollLeft > 0
        tableContainerRef.current.setAttribute(
          "data-scrolled",
          isScrolled.toString()
        )
      }
    }

    const container = tableContainerRef.current
    if (container) {
      container.addEventListener("scroll", handleScroll)
      handleScroll()
      return () => container.removeEventListener("scroll", handleScroll)
    }
  }, [tableContainerRef])

  useEffect(() => {
    if (filters.columns.length === 0) return

    setIsFetching(true)
    fetchProjectsList(filters, ROWS_PER_FETCH, 0, ignoreThreshold).then(
      (data) => {
        setIsFetching(false)
        if (!data) return

        if (!data.data.length) {
          setHasMore(false)
          setProjects([])
          return
        }

        if (filterItemId) {
          setProjects(
            data.data.filter((item: any) => item.data_id !== filterItemId)
          )
        } else {
          setProjects(data.data)
        }

        setOffset(ROWS_PER_FETCH)
        setHasMore(data.rows_before_limit_at_least! > ROWS_PER_FETCH)
      }
    )
  }, [filters, filterItemId, ignoreThreshold])

  const tableColumns = useMemo(() => {
    return [
      {
        field: "name",
        display: "Project",
        default_size: 270,
        sparkline: false,
        cell_data_type: "string",
        group: null,
        metric_slug: null,
        group_override: null,
        metadata: null,
      },
      ...filters.columns.map((column) => {
        const columnDefinition = columns.find(
          (c) => c.field === column.field
        ) as PresetColumnConfig

        if (columnDefinition) {
          return {
            ...columnDefinition,
            sparkline: column.sparkline,
            metadata: column.metadata,
          }
        }

        return column
      }),
    ]
      ?.map((column) => {
        if (!column) return null

        let field = column.field

        if (column.sparkline) {
          const fieldParts = column.field.split("_")
          const aggregation = fieldParts.pop()
          const interval = fieldParts.pop()
          const metric = fieldParts.join("_")

          field = `${metric}_${interval}_sparkline`
        }

        // TODO: Get ecosystem metrics in screener api
        if (!column.display) {
          const fieldParts = column.field.split("_")

          const aggregation = fieldParts.pop()
          const interval = fieldParts.pop()
          const metric = fieldParts.join("_")

          const metricName = metricDictionary.get(metric)?.title

          column.display = metricName
            ? `${capitalize(aggregation)} ${interval}`
            : undefined
          column.group = metricName

          const length = Math.max(
            column.group?.length ?? 0,
            column.display?.length ?? 0,
            column.field.length
          )

          column.default_size = Math.round(40 * Math.pow(length, 0.5))
        }

        const fieldKey = `${field}${column.sparkline ? "_sparkline" : ""}`

        return {
          id: fieldKey,
          accessorKey: field,
          header: column.display,
          group: column.group,
          minSize: column.default_size,
          group_override: column.group_override,
          cell: ({ row }: { row: TableRowType<any> }) => {
            const value = row.original[field as string]

            if (column?.sparkline && column?.metric_slug) {
              return (
                <Suspense fallback={null}>
                  <MiniChartInlineSparkline
                    value={value}
                    height={miniChartHeight}
                  />
                </Suspense>
              )
            }

            switch (field) {
              case "name":
                return (
                  <Link href={`/projects/${row.original.slug}`}>
                    <div className="flex gap-2.5 items-center justify-start">
                      <div
                        className="text-xs text-fg-secondary shrink-0"
                        style={{
                          minWidth: "1.9em",
                          textAlign: "center",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {row.index + 1}
                      </div>
                      {row.original.placeholder ? (
                        <Skeleton
                          className="rounded-full"
                          style={{
                            width: projectLogoSize,
                            height: projectLogoSize,
                          }}
                        />
                      ) : (
                        <ProjectLogo
                          logo={row.original.logo}
                          name={row.original.name}
                          size={projectLogoSize}
                          isRounded
                          className="shrink-0"
                        />
                      )}
                      <div className="flex gap-2 items-center justify-start w-0 flex-1">
                        {row.original.placeholder ? (
                          <Skeleton
                            style={{
                              width: row.index % 2 === 0 ? "90px" : "110px",
                              height: "14px",
                            }}
                          />
                        ) : (
                          <div
                            className="text-sm font-medium truncate"
                            title={row.original.name}
                          >
                            {row.original.name}
                          </div>
                        )}
                        {row.original?.symbol && (
                          <div
                            className={twMerge(
                              "text-fg-secondary text-sm",
                              fontSizeClassName
                            )}
                            style={{
                              fontFamily: "var(--font-geist-mono)",
                              opacity: 0.75,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {row.original.symbol}
                          </div>
                        )}
                        {row.original?.tier === "standard" && (
                          <StandardizedListingIcon />
                        )}
                      </div>
                    </div>
                  </Link>
                )
              case "definition":
                const projectSpecificMetricMetadata =
                  metricMetadataByProjectSlug[row.original.slug]

                const metricId = column.metadata?.metric_id

                const metricMetadata =
                  projectSpecificMetricMetadata?.metric_definitions?.find(
                    (m) => m.metric_id === metricId
                  )

                if (!metricMetadata) return null

                return (
                  <Tooltip
                    content={`Learn more about ${row.original.name} ${column.metadata?.metric_title}`}
                  >
                    <Tooltip.Trigger>
                      <Link
                        className="text-sm text-fg-secondary line-clamp-2 hover:underline"
                        href={`/projects/${row.original.slug}/metrics/${toSlug(metricId ?? "")}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {capitalizeFirstLetter(
                          metricMetadata?.definition || ""
                        )}
                      </Link>
                    </Tooltip.Trigger>
                  </Tooltip>
                )
              case "market_sectors":
                return row.original.placeholder ? (
                  <Skeleton
                    style={{
                      width: "100px",
                      height: "14px",
                    }}
                  />
                ) : (
                  <MarketSectorTooltip
                    marketSector={{
                      slug: row.original.market_sectors[0],
                      name:
                        marketSectorDictionary.get(
                          row.original.market_sectors[0]
                        )?.name || "",
                    }}
                  >
                    <Link
                      href={`/markets/${row.original.market_sectors[0]}`}
                      className="text-fg-secondary border-b border-dotted border-b-fg-secondary/50 truncate inline-block max-w-full leading-none mt-1.5"
                      onClick={(e) => {
                        e.stopPropagation()
                      }}
                    >
                      {
                        marketSectorDictionary.get(
                          row.original.market_sectors[0]
                        )?.name
                      }
                    </Link>
                  </MarketSectorTooltip>
                )
              case "market_sector_name":
              case "market_sector_id":
                return row.original.placeholder ? (
                  <Skeleton
                    style={{
                      width: "100px",
                      height: "14px",
                    }}
                  />
                ) : (
                  <Link
                    onClick={(e) => {
                      e.stopPropagation()
                    }}
                    href={`/markets/${row.original.market_sector_id}`}
                    className="text-fg-secondary hover:underline decoration-dotted whitespace-nowrap truncate w-full block"
                    title={row.original.market_sector_name}
                  >
                    {row.original.market_sector_name}
                  </Link>
                )
              case "listing_date":
                return (
                  <div
                    className={twMerge(tableNumberCss, fontSizeClassName)}
                    style={{
                      fontFamily: "var(--font-geist-mono)",
                    }}
                  >
                    {row.original.listing_date
                      ? new Date(row.original.listing_date)
                          .toISOString()
                          .split("T")[0]
                      : ""}
                  </div>
                )
              case "chain":
                return (
                  <div className="flex flex-col gap-6 max-w-full">
                    <div
                      className={twMerge(
                        "flex items-center",
                        fontSizeClassName
                      )}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation()
                      }}
                    >
                      {filters.chains?.length > 0 ? (
                        <SingleChainDisplay chainId={filters.chains[0]} />
                      ) : null}
                    </div>
                  </div>
                )
              case "chains":
                return (
                  <div className="flex flex-col gap-6 max-w-full">
                    <div
                      className={twMerge(
                        "flex items-center",
                        fontSizeClassName
                      )}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation()
                      }}
                    >
                      {row.original.chains?.length ? (
                        <ChainsDeployedOnLinkOrModal
                          chainIds={row.original.chains}
                          projectSlug={row.original.slug}
                        />
                      ) : null}
                    </div>
                  </div>
                )
              case "price_max_latest":
                return (
                  <LiveValueCell
                    fontSizeClassName={fontSizeClassName}
                    value={value}
                    projectId={row.original.project_id}
                    cellDataType={column?.cell_data_type ?? "number"}
                  />
                )
              default:
                let displayValue: string | JSX.Element = ""

                if (!field) return null

                // TODO: This is a temporary fix to show more accurate values for transaction fees
                if (field.includes("transaction_fee_")) {
                  displayValue =
                    CHART_VALUE_CELL_DATA_TYPES.accurateCurrencyThreeDecimals(
                      value
                    )
                } else {
                  displayValue = CHART_VALUE_CELL_DATA_TYPES[
                    column?.cell_data_type ?? "number"
                  ](value, numberFormat ?? DEFAULT_NUMBER_FORMAT)
                }

                return (
                  <div
                    className={twMerge(tableNumberCss, fontSizeClassName)}
                    style={{
                      fontFamily: "var(--font-geist-mono)",
                    }}
                  >
                    <span className="truncate">
                      {displayValue === "0" || displayValue === "0.0%"
                        ? ""
                        : displayValue}
                    </span>
                  </div>
                )
            }
          },
        }
      })
      .filter((c) => c !== null) as ColumnDef<any, any>[]
  }, [
    filters.columns,
    filters.chains,
    columns,
    metricDictionary,
    miniChartHeight,
    projectLogoSize,
    fontSizeClassName,
    metricMetadataByProjectSlug,
    marketSectorDictionary,
    numberFormat,
  ])

  const table = useReactTable({
    data: projects,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: "onChange",
    state: {
      sorting: filters.sorting,
      columnSizing,
    },
    initialState: {
      columnSizing,
    },
    onSortingChange: (sortingUpdater) => {
      tableContainerRef.current?.scrollTo({ top: 0, behavior: "instant" })

      const newSortVal =
        typeof sortingUpdater === "function"
          ? sortingUpdater(filters.sorting)
          : sortingUpdater

      setFilters((prev) => ({ ...prev, sorting: newSortVal }))
    },
    onColumnSizingChange: (columnSizingUpdater) => {
      const newColumnSizing =
        typeof columnSizingUpdater === "function"
          ? columnSizingUpdater(columnSizing)
          : columnSizingUpdater

      setColumnSizing(newColumnSizing)
    },
  })

  const { rows } = table.getRowModel()

  const fetchMore = useCallback(() => {
    if (isFetching) return

    if (hasMore) {
      setIsFetching(true)

      fetchProjectsList(filters, ROWS_PER_FETCH, offset).then((data) => {
        setIsFetching(false)
        if (!data) return

        if (!data.data.length) {
          setHasMore(false)
          return
        }

        const newOffset = offset + ROWS_PER_FETCH
        setOffset(newOffset)

        setProjects((prev) => [...prev, ...data.data])

        setHasMore(newOffset <= data.rows_before_limit_at_least!)
      })
    }
  }, [isFetching, setIsFetching, offset, filters, hasMore])

  const { ref: intersectionRef } = useIntersectionObserver({
    threshold: 0,
    rootMargin: "20%",
    onChange: (isIntersecting) => {
      if (isIntersecting) {
        fetchMore()
      }
    },
  })

  if (projects.length === 0 && !isFetching) {
    return (
      <EmptyState
        icon={MagnifyingGlass}
        headingText="No projects found"
        bodyText="There are no projects that match your search or filters."
      />
    )
  }

  return (
    <>
      <Card
        className="rounded-xl p-0"
        style={{
          overflow: "clip",
          borderBottomLeftRadius: "0px",
          borderBottomRightRadius: "0px",
        }}
      >
        <div ref={tableContainerRef} className="overflow-auto with-scrollbar">
          <CoreTable.Table
            width="100%"
            rowHover
            rowBorderRadius="none"
            density={compact ? "small" : "large"}
            className="screener-table"
            style={{
              tableLayout: "fixed",
            }}
          >
            <CoreTable.TableHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <CoreTable.Row key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHeader
                      key={header.column.id}
                      table={table}
                      header={header}
                      columns={columns}
                      compact={compact}
                      isSticking={false}
                    />
                  ))}
                </CoreTable.Row>
              ))}
            </CoreTable.TableHeader>
            <CoreTable.TableBody>
              {rows.map((row) => {
                const visibleCells = row.getVisibleCells()

                return (
                  <CoreTable.Row key={row.id} className="group/row">
                    {visibleCells.map((cell) => {
                      const isFirstColumn = cell.column.id === "name"

                      return (
                        <CoreTable.Cell
                          key={cell.id}
                          className={
                            isFirstColumn
                              ? "sticky left-0 z-10 bg-bg-surface group-hover/row:bg-[var(--row-hover-color)] pinned-column-shadow"
                              : ""
                          }
                          style={{
                            width: cell.column.getSize(),
                            maxWidth: cell.column.getSize(),
                            minWidth: cell.column.getSize(),
                          }}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </CoreTable.Cell>
                      )
                    })}
                  </CoreTable.Row>
                )
              })}
            </CoreTable.TableBody>
          </CoreTable.Table>
        </div>
      </Card>
      {hasMore && (
        <div
          className="flex items-center justify-center w-full gap-3"
          ref={intersectionRef}
        >
          <LoadingSpinner diameter={16} />
          <div className="text-sm text-fg-secondary">Loading more projects</div>
        </div>
      )}
    </>
  )
}
